import BasicRecord from "./BasicRecord";

type Recordable = string | number | symbol;

function isEmpty<T>(object: BasicRecord<T>): boolean {
    return Object.keys(object).length === 0;
}

function exists<T>(value: T | undefined | null): value is T {
    return value !== null && value !== undefined;
}

function keysOf<T>(object: T): (keyof T)[] {
    return Object.keys(object) as (keyof T)[];
}

function entriesOf<T extends Recordable, U>(object: Record<T, U>): [T, U][] {
    return Object.entries(object) as [T, U][];
}

function isAllPropertiesFilled<T>(object: T): boolean {
    return keysOf(object).every(key => object[key]);
}

function deleteEmptyProperties<T>(object: T) {
    keysOf(object).forEach(key => {
        if (!exists(object[key])) {
            delete object[key];
        }
    });
}

// returns a new object with the values at each key mapped using mapFn(value)
function objectMap<T extends Recordable, U, V>(object: Record<T, U>, mapFn: (value: U) => V): Record<T, V> {
    return keysOf(object).reduce((result, key) => {
        result[key] = mapFn(object[key]);
        return result;
    }, <Record<T, V>>{});
}

function sortObjectByKeys<T>(object: BasicRecord<T>): BasicRecord<T> {
    return Object.keys(object).sort().reduce((sortedObject, key) => {
        sortedObject[key] = object[key];
        return sortedObject;
    }, {} as BasicRecord<T>);
}

function objectEqual<K extends string | number | symbol, V>(
    object1: Record<K, V>,
    object2: Record<K, V>,
    compare: (item1: V, item2: V) => boolean = (item1, item2) => item1 === item2)
: boolean {
    if (object1 === object2) {
        return true;
    }

    const keys1 = keysOf(object1);
    const keys2 = keysOf(object2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !compare(object1[key], object2[key])) {
            return false;
        }
    }

    return true;
}

function findKeysOfUnequalValues<K extends string | number | symbol, V>(
    object1: Record<K, V>,
    object2: Record<K, V>,
    compare: (item1: V, item2: V) => boolean = (item1, item2) => item1 === item2)
: K[] {
    const keys1 = new Set(keysOf(object1));
    const keys2 = new Set(keysOf(object2));

    const unequalKeys: K[] = [];

    for (const key of keys1) {
        if (!keys2.has(key) || !compare(object1[key], object2[key])) {
            unequalKeys.push(key);
        }
    }

    return unequalKeys;
}

export {
    isEmpty,
    exists,
    keysOf,
    entriesOf,
    isAllPropertiesFilled,
    deleteEmptyProperties,
    objectMap,
    sortObjectByKeys,
    objectEqual,
    findKeysOfUnequalValues
};
